/**
 * MobileGoogleLogin — bridge page para autenticação Google da app móvel.
 *
 * Fluxo:
 * 1. Mobile abre `https://app.tectur.pt/mobile-google-login` num WebBrowser.
 * 2. Esta página detecta se já tem `#session_id=` no hash:
 *    - SE NÃO: redireciona para Emergent Google auth com `redirect` de volta.
 *    - SE SIM: troca o session_id por session_token via /api/auth/google-session,
 *      constrói `tectur4x4://auth-callback?token=...&email=...&name=...&user_id=...`
 *      e navega para lá — o browser mobile fecha automaticamente e passa o URL
 *      de volta à app.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEEP_LINK_SCHEME = 'tectur4x4';

const MobileGoogleLogin = () => {
  const [status, setStatus] = useState('A iniciar…');
  const [errorDetail, setErrorDetail] = useState(null);

  useEffect(() => {
    (async () => {
      // Extrai session_id do hash (formato #session_id=XYZ)
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const sessionId = params.get('session_id');

      if (!sessionId) {
        // Ainda não fomos autenticados — redireciona para Emergent Google auth
        setStatus('A abrir Google…');
        const redirectUrl = `${window.location.origin}/mobile-google-login`;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        return;
      }

      // Já viemos do callback com session_id — troca por session_token
      setStatus('A validar sessão…');
      try {
        const { data } = await axios.post(
          `${API}/auth/google-session`,
          { session_id: sessionId },
          { withCredentials: true },
        );
        const token = data.session_token;
        const user = data.user || {};
        setStatus('✓ Autenticado. A voltar à app…');

        // Constrói deep link para a app mobile capturar
        const q = new URLSearchParams({
          token: token || '',
          user_id: user.user_id || '',
          email: user.email || '',
          name: user.name || '',
          picture: user.picture || '',
          role: user.role || 'user',
        });
        const deep = `${DEEP_LINK_SCHEME}://auth-callback?${q.toString()}`;

        // Pequeno delay para o utilizador ver a mensagem
        setTimeout(() => {
          window.location.href = deep;
        }, 400);
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || 'Erro desconhecido';
        setStatus('Erro na autenticação');
        setErrorDetail(detail);
      }
    })();
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, Segoe UI, Roboto, Arial, sans-serif',
      background: '#0D5373',
      color: '#fff',
      padding: 24,
    }}>
      <div style={{ fontSize: 44, marginBottom: 12 }}>🔐</div>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Tectur 4x4</h1>
      <p style={{ fontSize: 16, marginTop: 20, opacity: 0.9 }}>{status}</p>
      {errorDetail && (
        <p style={{ fontSize: 12, marginTop: 12, opacity: 0.7, maxWidth: 340, textAlign: 'center' }}>
          {errorDetail}
        </p>
      )}
      <div style={{
        width: 40, height: 40, marginTop: 22,
        border: '3px solid rgba(255,255,255,0.25)',
        borderTopColor: '#fff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
};

export default MobileGoogleLogin;
